import {
  Component, DestroyRef,
  EventEmitter, inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from "@angular/material/dialog";
import { BehaviorSubject } from "rxjs";

import { LorenModalData } from "../../../loren-dialog/interfaces/loren-modal-data.interface";
import { LorenEntityFormModel } from "../../../loren-entity-form-card/interfaces/loren-entity-form-model.interface.ts";
import { LorenFormField } from "../../../loren-entity-form-card/interfaces/loren-form-field.interface";
import { LorenDialogComponent } from "../../../loren-dialog/components/loren-dialog/loren-dialog.component";
import { LorenFieldTypes } from "../../../loren-entity-form-card/enums/loren-field-types.enum";
import { LorenLayer } from "../../../../services/loren-layer-service/interfaces/loren-layer.interface";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "loren-layer-selection-dialog",
    templateUrl: "./loren-layer-selection-dialog.component.html",
    styleUrls: ["./loren-layer-selection-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LorenLayerSelectionDialogComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);

  private readonly destroyRef = inject(DestroyRef);
  @Input() layers: LorenLayer[] = [];
  @Input() continueBtnLabel = "operations.continue";
  @Input() cancelBtnLabel = "operations.cancel";
  @Input() dialogTitle = "operations.choose_assets";
  @Input() dialogSubTitle = "";
  @Input() defaultValues: LorenLayer[] = [];
  @Output() finishSelection = new EventEmitter<LorenLayer[]>();
  @Output() cancelSelection = new EventEmitter<void>();

  private dialogConfig: MatDialogConfig<LorenModalData>;
  private entitySelectionFormModel: LorenEntityFormModel;
  private allAssetsFormFields: LorenFormField[] = [];
  private reloadSubject = new BehaviorSubject<null>(null);
  private selectedLayers: LorenLayer[] = [];
  private dialogRef: MatDialogRef<LorenDialogComponent>;

  ngOnInit(): void {
    this.showAssetListDialog();
  }

  ngOnDestroy(): void {
    this.dialogRef.close();
  }

  showAssetListDialog() {
    this.entitySelectionFormModel = this.prepareEntitySelectionFormModel();
    this.selectedLayers = [];

    const entitiesListDialog: LorenModalData = {
      form: this.entitySelectionFormModel,
      modalAcceptBtnLabel: this.continueBtnLabel,
      modalCloseBtnLabel: this.cancelBtnLabel,
      title: this.dialogTitle,
      modalSubTitle: this.dialogSubTitle,
    };

    this.prepareDialog(entitiesListDialog).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((result) => {
      if (!result) {
        this.cancelSelection.emit();
        return;
      }

      this.selectedLayers = this.layers.filter((x) => {
        return this.entitySelectionFormModel.formFields
          .filter((f) => Boolean(f.value) === true)
          .find((f) => Number(f.name) === x.id);
      });

      this.finishSelection.emit(this.selectedLayers);
    });
  }

  private prepareEntitySelectionFormModel() {
    const formModel: LorenEntityFormModel = {
      formFields: [],
      formName: "",
      reloadForm: this.reloadSubject,
    };

    if (this.layers.length > 1) {
      formModel.formFields.push({
        name: "searchEntity",
        label: "operations.search",
        placeholder: "operations.input_layer_name",
        formControlType: LorenFieldTypes.INPUT,
      });
    }

    if (this.layers.length > 1) {
      formModel.formFields.push({
        name: "selectAll",
        label: "operations.select_all",
        look: "mb-3",
        formControlType: LorenFieldTypes.CHECKBOX,
      });
    }

    this.prepareAllLayersSelectionFields();

    formModel.formFields.push(...this.allAssetsFormFields);

    return formModel;
  }

  private prepareAllLayersSelectionFields() {
    this.allAssetsFormFields = [];
    this.layers.forEach((asset) => {
      const assetField: LorenFormField = {
        name: String(asset.id),
        label: `${asset.name}`,
        value: this.defaultValues.indexOf(asset) !== -1,
        formControlType: LorenFieldTypes.CHECKBOX,
      };

      this.allAssetsFormFields.push(assetField);
    });
  }

  private prepareDialog(modalData: LorenModalData) {
    this.dialogConfig = new MatDialogConfig();
    this.dialogConfig.disableClose = true;
    this.dialogConfig.autoFocus = true;
    this.dialogConfig.data = modalData;
    this.dialogConfig.hasBackdrop = false;

    this.dialogRef = this.dialog.open(LorenDialogComponent, this.dialogConfig);

    this.handleFormInteractions(modalData);

    return this.dialogRef.afterClosed();
  }

  private handleFormInteractions(modalData: LorenModalData) {
    modalData.form.formFieldInteraction.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((x) => {
      if (!x) {
        return;
      }

      const searchField = this.entitySelectionFormModel.formFields.find(
        (f) => f.formControlType === LorenFieldTypes.INPUT
      );
      const selectAllEntities = this.entitySelectionFormModel.formFields.find(
        (f) => f.name === "selectAll"
      );

      if (x.fieldName !== "selectAll" && x.fieldCurrentValue === false) {
        this.entitySelectionFormModel.formFields.find(
          (f) => f.name === "selectAll"
        ).value = false;
      }

      if (x.fieldName === "searchEntity") {
        let entityFields = this.allAssetsFormFields.filter(
          (f) =>
            f.formControlType === LorenFieldTypes.CHECKBOX &&
            f.label.toLowerCase().includes(x.fieldCurrentValue.toLowerCase())
        );

        if (entityFields.length === 0) {
          entityFields = [];
        }

        entityFields.unshift(selectAllEntities);
        entityFields.unshift(searchField);

        this.entitySelectionFormModel.formFields = entityFields;
        this.reloadSubject.next(null);
      }

      if (x.fieldName === "selectAll") {
        const assetFields = this.entitySelectionFormModel.formFields.filter(
          (f) => f.formControlType === LorenFieldTypes.CHECKBOX
        );

        assetFields.forEach((f) => (f.value = x.fieldCurrentValue));
        assetFields.unshift(searchField);
        this.reloadSubject.next(null);
      }
    });
  }
}
